/**
 * Ball state, as a structure of arrays.
 *
 * One `Float64Array` per component rather than an array of `{x, y}` objects, because
 * the stepping loop must allocate nothing: 11 balls × 7,200 steps would be ~80k
 * garbage objects per shot, and a GC pause is the one thing that breaks a frame
 * budget unpredictably (ARCHITECTURE.md §5.3).
 *
 * Iteration is ALWAYS by ascending index, which is also ball id. Floating-point
 * addition is not associative, so a different order is a different answer.
 */
import type { BallSnapshot, Layout } from './types.ts';

export interface TableState {
  /** Number of balls, including the cue ball at index 0. */
  readonly count: number;
  readonly xM: Float64Array;
  readonly yM: Float64Array;
  readonly vxMps: Float64Array;
  readonly vyMps: Float64Array;
  /** Angular velocity is 3D even though position is 2D — see ARCHITECTURE.md §5.3. */
  readonly wxRadS: Float64Array;
  readonly wyRadS: Float64Array;
  readonly wzRadS: Float64Array;
  /** 0 = on the table, 1 = potted. `Uint8Array` so the state hashes exactly. */
  readonly potted: Uint8Array;
}

function allocate(count: number): TableState {
  return {
    count,
    xM: new Float64Array(count),
    yM: new Float64Array(count),
    vxMps: new Float64Array(count),
    vyMps: new Float64Array(count),
    wxRadS: new Float64Array(count),
    wyRadS: new Float64Array(count),
    wzRadS: new Float64Array(count),
    potted: new Uint8Array(count),
  };
}

/** Build a state from a layout. The cue ball becomes id 0; object balls follow. */
export function createState(layout: Layout): TableState {
  const state = allocate(1 + layout.balls.length);
  state.xM[0] = layout.cue.xM;
  state.yM[0] = layout.cue.yM;
  for (let i = 0; i < layout.balls.length; i++) {
    const ball = layout.balls[i];
    if (ball === undefined) continue;
    state.xM[i + 1] = ball.xM;
    state.yM[i + 1] = ball.yM;
  }
  return state;
}

/** Rebuild a state from snapshots — used to chain shots and to replay a shot log. */
export function stateFromSnapshots(balls: readonly BallSnapshot[]): TableState {
  const state = allocate(balls.length);
  for (let i = 0; i < balls.length; i++) {
    const ball = balls[i];
    if (ball === undefined) continue;
    state.xM[i] = ball.xM;
    state.yM[i] = ball.yM;
    state.vxMps[i] = ball.vxMps;
    state.vyMps[i] = ball.vyMps;
    state.wxRadS[i] = ball.wxRadS;
    state.wyRadS[i] = ball.wyRadS;
    state.wzRadS[i] = ball.wzRadS;
    state.potted[i] = ball.potted ? 1 : 0;
  }
  return state;
}

export function cloneState(state: TableState): TableState {
  return {
    count: state.count,
    xM: state.xM.slice(),
    yM: state.yM.slice(),
    vxMps: state.vxMps.slice(),
    vyMps: state.vyMps.slice(),
    wxRadS: state.wxRadS.slice(),
    wyRadS: state.wyRadS.slice(),
    wzRadS: state.wzRadS.slice(),
    potted: state.potted.slice(),
  };
}

/** Plain-data view of the state, safe to serialise and to hand across a boundary. */
export function snapshot(state: TableState): BallSnapshot[] {
  const balls: BallSnapshot[] = [];
  for (let i = 0; i < state.count; i++) {
    balls.push({
      id: i,
      xM: state.xM[i] ?? 0,
      yM: state.yM[i] ?? 0,
      vxMps: state.vxMps[i] ?? 0,
      vyMps: state.vyMps[i] ?? 0,
      wxRadS: state.wxRadS[i] ?? 0,
      wyRadS: state.wyRadS[i] ?? 0,
      wzRadS: state.wzRadS[i] ?? 0,
      potted: state.potted[i] === 1,
    });
  }
  return balls;
}
